import React, { useState } from 'react';
import NoteList from '../../NoteList';
import { useMutation } from '@apollo/client/react';
import { ADD_LOAD_NOTES, EDIT_LOAD_NOTES } from '../../../graphql/client/mutations';
import { AddLoadNotes, EditLoadNotes } from '../../../types/schemaTypes';
import { GET_LOAD_NOTES } from '../../../graphql/client/queries';
import { Query } from '../../_common';
import { Error, Loading } from '../../_common';
import { Note } from '../../../interfaces/Notes/note';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import {  toast } from 'react-toastify';
import {toastOpt} from '../../../utils/helpers/toastHelper'

interface LoadNotesProps {
    loadId: number;
}

// TODO Check the backend query for this value
// Do not change this value unless the associate backend value has been updated - (5/11/19) - KS
const PAGE_SIZE = 50;
const SuccessMessage = {
    save: 'Notes added successfully',
    update:'Notes updated successfully'
};


const LoadNotes = ({ loadId }: LoadNotesProps): React.ReactElement => {
    const [isEndOfResults, setIsEndOfResults] = useState(false);
    const authContext = useAuthContext();
    const canAddNewNotes = authContext.userHasPermission(PermissionEnum.AddLoadNotes);
    const canViewNotesList = authContext.userHasPermission(PermissionEnum.ViewLoadNotes);
    const canEditLoadNotes = useAuthContext().userHasPermission(PermissionEnum.EditLoadNotes);
    const onLoadNotesAddorEditFail = errorMessage => {
        const alertMessage = errorMessage[0].includes('Not permitted to update the load notes created by another user') ? 'This note can only be edited by the user who created it. You can add another note instead' : errorMessage[0]; 
        toast.error(alertMessage,toastOpt);
        //refetch();
    };
    const onLoadNotesAddorEditSuccess = (action) =>{
        toast.success(SuccessMessage[action],toastOpt);
    }
    const [addLoadNotes] = useMutation<AddLoadNotes>(ADD_LOAD_NOTES, {
        onError: (error) => {console.log("error",error)},
        onCompleted: (data) => { 
            if (data.addLoadNotes === null) return;
            const addLoadNotesError = (data.addLoadNotes.errors && data.addLoadNotes.errors.length > 0) ? data.addLoadNotes.errors : null ;
            if (addLoadNotesError){
                
                onLoadNotesAddorEditFail(addLoadNotesError)
            } else {
                
                onLoadNotesAddorEditSuccess('save');
            }
        
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_NOTES,
                    variables: { loadId: loadId },
                },
            ];
        },
    });
    const [editLoadNotes] = useMutation<EditLoadNotes>(EDIT_LOAD_NOTES, {
        onError: (error) => {console.log("error",error)},
        onCompleted: (data) => { 
            if (data.editLoadNotes === null) return;
            const editNoteErrors = (data.editLoadNotes.errors && data.editLoadNotes.errors.length > 0) ? data.editLoadNotes.errors : null ;
            if (editNoteErrors){
                
                onLoadNotesAddorEditFail(editNoteErrors)
            } else {
                
                onLoadNotesAddorEditSuccess('update');
            }
        
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_NOTES,
                    variables: { loadId: loadId },
                },
            ];
        },
    });

    const onSaveNote = (noteText: string): void => {
        const addLoadNoteInput = {
            notes: noteText,
            loadId: loadId,
        };
        addLoadNotes({ variables: { input: addLoadNoteInput } });
    };

    const onEditNote = (noteText: string, noteId: number): void => {
        const editLoadNoteInput = {
            id: noteId,
            newValues: {
                notes: noteText,
            },
        };
        editLoadNotes({ variables: { input: editLoadNoteInput } });
    };

    return (
        <Query
            query={GET_LOAD_NOTES}
            variables={{
                loadId: loadId,
            }}
        >
            {({ loading, error, data, fetchMore }: any): React.ReactElement | null => {

                const offset = data && data.getLoadNotes ? data.getLoadNotes.length / PAGE_SIZE : 0;

                React.useEffect(() => {
                    if (
                        offset % 1 !== 0 ||
                        (!isEndOfResults && data && data.getLoadNotes && data.getLoadNotes.length < PAGE_SIZE)
                    ) {
                        // If offset is not a whole number, the end of the results has been reached. (5/11/19) - KS
                        setIsEndOfResults(true);
                    }
                }, [offset, data?.getLoadNotes?.length]);

                if (loading) {
                    return <Loading />;
                }

                if (error) {
                    return <Error error={error} />;
                }

                if (data && data.getLoadNotes) {
                    const notesList: Note[] = [];
                    if (data.getLoadNotes.length > 0) {
                        data.getLoadNotes.forEach((item: any) => {
                            const note: Note = {
                                noteId: item.id,
                                notes: item.notes,
                                createdAtUtc: item.createdAt,
                                modifiedAtUtc: item.modifiedAt,
                                createdUserId: item.createdUserId,
                                entityId: item.loadId,
                                isDeleted: item.isDeleted,
                                name: item.name,
                                userName: item.userName,
                                email: item.email,
                            };
                            notesList.push(note);
                        });
                    }

                    return (
                        <NoteList
                            isLoading={loading}
                            isEndOfResults={isEndOfResults}
                            onLoadMore={() =>
                                fetchMore({
                                    variables: {
                                        offset,
                                    },
                                    updateQuery: (prev: any, { fetchMoreResult }: any) => {
                                        if (
                                            prev &&
                                            fetchMoreResult &&
                                            prev.getLoadNotes &&
                                            fetchMoreResult.getLoadNotes
                                        ) {
                                            return Object.assign({}, prev, {
                                                getLoadNotes: [...prev.getLoadNotes, ...fetchMoreResult.getLoadNotes],
                                            });
                                        } else {
                                            return prev;
                                        }
                                    },
                                })
                            }
                            noteItems={notesList}
                            onSaveNote={onSaveNote}
                            onEditNote={onEditNote}
                            canAddNewNotes={canAddNewNotes}
                            canViewNotesList={canViewNotesList}
                            canEditNote={canEditLoadNotes}
                        />
                    );
                } else {
                    return null;
                }
            }}
        </Query>
    );
};

export default LoadNotes;
